import { AxiosError } from 'axios'
import { uniqueId } from 'lodash'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils'
import { downloadFile } from 'Shared/utils/file'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, TransformArrayBufferResponseType } from 'Core/interfaces'
import {
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue,
	NameCodeToLabelValue
} from 'Core/utils'
import { getFilterValue, prepareGenericParams } from 'Core/utils/storeHelpers'

import { Audit, AuditFilterFields, Kpi } from 'Portals/Customer/entities'
import { AuditRepo, KpiRepo } from 'Portals/Customer/repositories'

import { RootStore } from '../rootStore/RootStore'

import { AuditTable } from './AuditTable'

export class AuditStore {
	table: AuditTable
	@observable accessor dailyCount = getDefaultStoreValue<number>()
	@observable accessor dailyData = getDefaultStoreArrayValue<Audit.Daily>()
	@observable accessor kpiMetricFilterOptions = getDefaultStoreArrayValue<Kpi.Metric.FilterOptions>()
	@observable accessor isExportingLoading: boolean = false

	@computed
	get mappedDailyData() {
		return this.dailyData.value.map((val) => ({
			...val,
			id: uniqueId()
		}))
	}

	@computed
	get isExportDisabled() {
		const isAnyFilterApplied = Object.values(this.table.filters).some(
			(filter) => !!getFilterValue(filter.data)
		)
		const isAnySearchApplied = this.table.searchFilters.some((filter) => !!filter.data)
		const hasSelectedRows = this.table.hasSelectedRows
		return !isAnyFilterApplied && !isAnySearchApplied && !hasSelectedRows
	}

	@computed
	get enteredToday() {
		return this.dailyCount.value || 0
	}

	@computed
	get kpiCategoryOptions() {
		return this.kpiMetricFilterOptions.value.map((val) => NameCodeToLabelValue(val.category))
	}

	@computed
	get kpiOptions() {
		const selectedCategory = this.table.filters[AuditFilterFields.KpiCategoryCode]?.data
		const correctKpiCategory = getFilterValue(selectedCategory)
		return this.kpiMetricFilterOptions.value.reduce((acc, val) => {
			if (!correctKpiCategory) {
				val.kpis.forEach((kpi) => {
					acc.push(entityToStringLabelValue(kpi))
				})
				return acc
			}

			if (val.category.code === correctKpiCategory) {
				acc = val.kpis.map(entityToStringLabelValue)
			}
			return acc
		}, [] as LabelValue[])
	}

	constructor(private rootStore: RootStore) {
		this.table = new AuditTable(rootStore)
	}

	@flow.bound
	*exportToXlsx(): CustomGenerator<TransformArrayBufferResponseType> {
		this.isExportingLoading = true

		try {
			const params = prepareGenericParams(this.table)
			const document = yield AuditRepo.exportAudit(params)
			const { data, headers } = document
			downloadFile(
				data,
				undefined,
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
				headers
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isExportingLoading = false
		}
	}

	@flow.bound
	*retrieveSuccessfullySavedCount(): CustomGenerator<number> {
		yield requestWrapper({
			field: this.dailyCount,
			repository: AuditRepo.retrieveSuccessfullySavedCount,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDailyData(): CustomGenerator<Audit.Daily> {
		yield requestWrapper({
			field: this.dailyData,
			repository: AuditRepo.retrieveDailyData,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveMetricFilterOptions(): CustomGenerator<Kpi.Metric.FilterOptions[]> {
		yield requestWrapper({
			field: this.kpiMetricFilterOptions,
			repository: KpiRepo.retrieveMetricFilterOptions,
			repositoryParams: null
		})
	}

	@action.bound
	onChangeKpiCategoryFilter() {
		delete this.table.filters[AuditFilterFields.kpiId]
	}

	@action.bound
	onChangeKpiFilter() {
		const kpiId = this.table.filters[AuditFilterFields.kpiId]?.data

		if (!!this.table.filters[AuditFilterFields.KpiCategoryCode]?.data || !kpiId) {
			return
		}
		const correctKpi = getFilterValue(kpiId)
		if (!correctKpi) {
			return
		}
		const category = this.kpiMetricFilterOptions.value.reduce((acc, val) => {
			if (val.kpis.find((kpi) => kpi.id.toString() === correctKpi.toString())) {
				acc = val.category.code
			}
			return acc
		}, '' as string)
		this.table.setFilter({
			...this.table.filters[AuditFilterFields.KpiCategoryCode],
			data: category
		})
	}
}
